/**
 * The characters a FILED company name may carry: English letters, digits,
 * space and & - . , '. The designator (LLC, Inc, …) is stored separately.
 *
 * Owner rule V5 (2026-09-21) and the state research in the spec: Wyoming sends
 * names with special characters to paper review, Delaware §102(a)(1) allows
 * foreign words only "written in Roman characters", and the IRS name control
 * keeps only A-Z, 0-9, & and -. Periods, commas and apostrophes are kept here
 * because real registered names carry them ("AT&T Co., Ltd.", "Ben & Jerry's").
 *
 * Its own module so `company.ts` and `shareholders.ts` can both import it
 * without importing each other.
 */

import { transliterateToEnglish } from "./person-name";

// The specified class escapes the hyphen. It is literal at the end of the class,
// which is why recommended eslint calls the escape useless.
// eslint-disable-next-line no-useless-escape
export const FILING_COMPANY_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 &.,'\-]*$/;

/** Copy for a filed company name that fails {@link FILING_COMPANY_NAME_PATTERN}. */
export const FILING_COMPANY_NAME_MESSAGE =
  "Company name can only contain English letters (A-Z), numbers, spaces and & - . , ' because US state filings cannot accept other characters";

/** Blank passes: the required rule reports that separately. */
export function hasFilingSafeCompanyNameCharacters(
  name: string | null | undefined,
): boolean {
  if (typeof name !== "string") return true;
  const trimmed = name.trim();
  return trimmed.length === 0 || FILING_COMPANY_NAME_PATTERN.test(trimmed);
}

/**
 * The spelling to offer for a company name that fails the filing rule, or null
 * when there is nothing useful to offer (already valid, or a script or symbol
 * transliteration cannot fix). Same idea as `englishNameSuggestion`.
 */
export function filingCompanyNameSuggestion(
  value: string | null | undefined,
): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.length === 0 || FILING_COMPANY_NAME_PATTERN.test(trimmed))
    return null;
  const candidate = transliterateToEnglish(trimmed).replace(/\s+/g, " ");
  return FILING_COMPANY_NAME_PATTERN.test(candidate) ? candidate : null;
}
